import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ChartThemeProperty } from '@nimbus-library/features/charts/models/chart-theme-property.enum';
import { ChartProperties } from '@nimbus-library/features/charts/models/chart.model';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { ChatChartType } from '@nimbus-global/features/chat-chart/models/chat-chart.model';
import { CompanionToolCallRecord, CompanionToolName } from '@nimbus-library/features/companion-chat/models/companion-conversation.model';
import { ICompanionChatBuilder } from './companion-chat-builder.interface';

interface IOutcomeMapping {
  keys: string[];
  labelKey: TranslationKeys;
  colorId: ChartThemeProperty;
}

const MAPPINGS: IOutcomeMapping[] = [
  { keys: ['Handled'], labelKey: TranslationKeys.ChartLabelHandledCalls, colorId: ChartThemeProperty.Handled },
  { keys: ['HangupBeforeAccept'], labelKey: TranslationKeys.ChartLabelHangupBeforeAccept, colorId: ChartThemeProperty.HangupBeforeAccept },
  { keys: ['HangupInIvr'], labelKey: TranslationKeys.ChartLabelHangupInIVR, colorId: ChartThemeProperty.HangupInIvr },
  {
    keys: ['CustomerHangupInIvrAfterQueue'],
    labelKey: TranslationKeys.ChartLabelHangupInIVRAfterQueue,
    colorId: ChartThemeProperty.HangupInIVRAfterQueue,
  },
  { keys: ['HangupInQueue'], labelKey: TranslationKeys.ChartLabelHangupInQueue, colorId: ChartThemeProperty.HangupInQueue },
  {
    keys: ['DeclinedByWorkflowWhenNoQueue', 'DeclinedByWorkflowAfterQueue'],
    labelKey: TranslationKeys.ChartLabelHangupByWorkflow,
    colorId: ChartThemeProperty.HangupByWorkflow,
  },
  {
    keys: ['WorkflowInternalTransferSuccessful', 'WorkflowExternalTransferSuccessful'],
    labelKey: TranslationKeys.ChartLabelTransferredByWorkflow,
    colorId: ChartThemeProperty.TransferredByWorkflow,
  },
  {
    keys: ['UserInternalTransferSuccessful', 'UserExternalTransferSuccessful'],
    labelKey: TranslationKeys.ChartLabelTransferredByUser,
    colorId: ChartThemeProperty.TransferredByUser,
  },
  {
    keys: ['WorkflowInternalTransferFailed', 'WorkflowExternalTransferFailed', 'UserInternalTransferFailed', 'UserExternalTransferFailed'],
    labelKey: TranslationKeys.ChartLabelTransferFailed,
    colorId: ChartThemeProperty.TransferFailed,
  },
  {
    keys: ['OutboundTaskDestinationAccepted'],
    labelKey: TranslationKeys.ChartLabelDestinationAccepted,
    colorId: ChartThemeProperty.DestinationAccepted,
  },
  {
    keys: ['OutboundTaskDestinationDeclined'],
    labelKey: TranslationKeys.ChartLabelDestinationDeclined,
    colorId: ChartThemeProperty.DestinationDeclined,
  },
  {
    keys: ['OutboundTaskDestinationNotReached'],
    labelKey: TranslationKeys.ChartLabelDestinationNotReached,
    colorId: ChartThemeProperty.DestinationNotReached,
  },
  { keys: ['OutboundTaskUserAborted'], labelKey: TranslationKeys.ChartLabelUserAborted, colorId: ChartThemeProperty.UserAborted },
  { keys: ['OutboundTaskLostInQueue'], labelKey: TranslationKeys.ChartLabelLostInQueue, colorId: ChartThemeProperty.LostInQueue },
  {
    keys: [
      'ExternalSystemCancelledBeforeAccept',
      'ExternalSystemCancelled',
      'ExternalSystemCancelledAfterQueue',
      'ExternalSystemCancelledInQueue',
    ],
    labelKey: TranslationKeys.ChartLabelCanceledByExternalSystem,
    colorId: ChartThemeProperty.CanceledByExternalSystem,
  },
  {
    keys: ['MarkedAsHandledByWorkflow'],
    labelKey: TranslationKeys.ChartLabelMarkedAsHandledByWorkflow,
    colorId: ChartThemeProperty.MarkedAsHandledByWorkflow,
  },
  {
    keys: ['MarkedAsHandledByUser'],
    labelKey: TranslationKeys.ChartLabelMarkedAsHandledByUser,
    colorId: ChartThemeProperty.MarkedAsHandledByUser,
  },
  {
    keys: ['StandbyDutyExternalAccepted'],
    labelKey: TranslationKeys.ChartLabelStandbyDutyExternalAccepted,
    colorId: ChartThemeProperty.StandbyDutyAccepted,
  },
  {
    keys: ['WorkflowConversationRecorded'],
    labelKey: TranslationKeys.ChartLabelConversationRecorded,
    colorId: ChartThemeProperty.Voicemail,
  },
  {
    keys: ['WorkflowCallbackScheduleSuccessful'],
    labelKey: TranslationKeys.ChartLabelCallbackScheduleSuccess,
    colorId: ChartThemeProperty.MarkedAsHandledByWorkflow,
  },
  { keys: ['Failed'], labelKey: TranslationKeys.ChartLabelFailed, colorId: ChartThemeProperty.NotHandled },
];

@Injectable({ providedIn: 'root' })
export class OutcomeChartBuilder implements ICompanionChatBuilder {
  public readonly toolName = CompanionToolName.GetServiceConversationCountsByOutcome;
  public readonly chartType = ChatChartType.Pie;

  public constructor(private readonly translateService: TranslateService) {}

  public build(call: CompanionToolCallRecord): ChartProperties {
    const data = (call.metadata as { data?: { countsByOutcome?: Record<string, number> } })?.data?.countsByOutcome;
    if (!data) {
      return null;
    }
    const records = MAPPINGS.filter((m) => m.keys.some((k) => data[k] !== undefined)).map((m) => ({
      data: m.keys.reduce((acc, k) => acc + (data[k] ?? 0), 0),
      label: this.translateService.instant(m.labelKey),
      colorId: m.colorId,
      visibleInLegend: true,
    }));
    if (!records.length) {
      return null;
    }
    const aggregatedValue = records.reduce((acc, r) => acc + r.data, 0);
    return { series: [{ records }], aggregatedValue };
  }
}
